/**
 * Wires `catalogue.model.ts` to its one read and its one irreversible write.
 *
 * Calls `fetchEstateCatalogue` and signs the thumbnails in the same pass, and
 * `declareCatalogueComplete` on the second press of the declare button. The
 * search and chip filters are local and never re-ask the server.
 *
 * The state the model cannot hold is the query, the chip, the armed flag for
 * the two-press declaration, and the generation counter bumped after a
 * declaration so the banner re-reads what is now true rather than assuming
 * the write took.
 */

import { useEffect, useState } from "react";
import type { CatalogueMeta, CatalogueWork } from "@ondo/shared";

import { ApiError, declareCatalogueComplete, fetchEstateCatalogue } from "../lib/api.ts";
import { signWorkThumbnails } from "../lib/workImages.ts";
import { CATALOGUE_COPY } from "./estate.constants.ts";
import { toCatalogueView } from "./catalogue.model.ts";
import type { CatalogueChip, CatalogueView } from "./catalogue.model.ts";

interface Loaded {
  catalogues: CatalogueMeta[];
  works: CatalogueWork[];
  thumbs: Map<string, string>;
}

/**
 * One read, filtered locally. The chips and the search never re-ask the
 * server, because the rows they narrow are the rows the header counted — two
 * requests could disagree about what "14 works" means.
 */
export function useCatalogueController(input: { workBase: string; canDeclare: boolean }) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [chip, setChip] = useState<CatalogueChip>("all");
  const [declaring, setDeclaring] = useState(false);
  /**
   * The first press arms; the second fires. An inline pair rather than a
   * browser confirm, the viewing rooms' own two-press pattern — a one-way act
   * gets a second press naming what cannot be undone, in the page's words.
   */
  const [armed, setArmed] = useState(false);
  /** Bumped after a declaration so the banner re-reads what is now true. */
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let active = true;

    fetchEstateCatalogue()
      .then(async (body) => {
        const thumbs = await signWorkThumbnails(
          body.works.map((work) => ({ id: work.workId, imagePath: work.imagePath })),
        );
        if (active) setLoaded({ catalogues: body.catalogues, works: body.works, thumbs });
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(cause instanceof ApiError ? cause.message : CATALOGUE_COPY.errorTitle);
      });

    return () => {
      active = false;
    };
  }, [generation]);

  const declare = async (artistId: string) => {
    if (!armed) {
      setArmed(true);
      return;
    }

    setArmed(false);
    setDeclaring(true);
    try {
      await declareCatalogueComplete(artistId);
      setGeneration((n) => n + 1);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : CATALOGUE_COPY.errorTitle);
    } finally {
      setDeclaring(false);
    }
  };

  const view: CatalogueView = error
    ? { kind: "error", title: CATALOGUE_COPY.errorTitle, message: error }
    : loaded === null
      ? { kind: "loading", message: CATALOGUE_COPY.loading }
      : toCatalogueView({
          catalogues: loaded.catalogues,
          works: loaded.works,
          query,
          chip,
          canDeclare: input.canDeclare,
          workBase: input.workBase,
        });

  return {
    view,
    thumbs: loaded?.thumbs ?? new Map<string, string>(),
    query,
    setQuery,
    chip,
    setChip,
    declare,
    declaring,
    declareArmed: armed,
    disarmDeclare: () => setArmed(false),
  };
}
